import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { AbsenceApproval } from '@/api/model'
import type { AbsenceModel, OrderType } from '@/api/model'
import { ABSENCE_STATUS_CLASS, getAbsenceReasonLabel, getAbsenceStatusLabel } from './absenceLabels'

/** Vom Paginated-Endpunkt akzeptierte Sortierfelder (s. altes Portal). */
export const AbsenceSortFields = {
  EmployeeName: 'employeeName',
  StartTimestamp: 'startTimestamp',
  EndTimestamp: 'endTimestamp',
} as const

interface Props {
  rows: AbsenceModel[] | undefined | null
  isLoading: boolean
  isError: boolean
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
  updatingId: number | null
  onUpdateStatus: (absence: AbsenceModel, status: AbsenceApproval) => void
  /** Approve/reject visible only with ServiceModules.Edit on the module. */
  canEdit: boolean
}

const COL_COUNT = 7

const formatDate = (value: string) => dayjs(value).format('DD.MM.YYYY')

export function AbsenceEntriesDataTable({
  rows,
  isLoading,
  isError,
  currentSort,
  currentOrder,
  onSort,
  updatingId,
  onUpdateStatus,
  canEdit,
}: Props) {
  const { t, i18n } = useTranslation(['service-modules-absence', 'service-modules', 'common'])
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={AbsenceSortFields.EmployeeName} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('columns.employee', { ns: 'common' })}
          </SortableTableHead>
          <SortableTableHead field={AbsenceSortFields.StartTimestamp} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('shared.columns.start', { ns: 'service-modules' })}
          </SortableTableHead>
          <SortableTableHead field={AbsenceSortFields.EndTimestamp} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('shared.columns.end', { ns: 'service-modules' })}
          </SortableTableHead>
          <TableHead>{t('absence.table.duration')}</TableHead>
          <TableHead>{t('absence.table.reason')}</TableHead>
          <TableHead>{t('columns.status', { ns: 'common' })}</TableHead>
          <TableHead className="text-right">{t('columns.actions', { ns: 'common' })}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={!rows || rows.length === 0}
          colSpan={COL_COUNT}
          errorMessage={t('absence.table.loadError')}
          emptyMessage={t('absence.table.empty')}
        >
          {rows?.map((absence) => {
          const isUpdating = updatingId === absence.id
          return (
            <TableRow key={absence.id}>
              <TableCell>{absence.employeeName || '—'}</TableCell>
              <TableCell className="tabular-nums">{formatDate(absence.startTimestamp)}</TableCell>
              <TableCell className="tabular-nums">{formatDate(absence.endTimestamp)}</TableCell>
              <TableCell className="tabular-nums">
                {absence.durationInDays == null
                  ? '—'
                  : t('absence.table.durationDays', {
                      count: absence.durationInDays,
                      value: absence.durationInDays.toLocaleString(i18n.language),
                    })}
              </TableCell>
              <TableCell>{getAbsenceReasonLabel(t, absence.absenceReason)}</TableCell>
              <TableCell>
                <Badge className={ABSENCE_STATUS_CLASS[absence.absenceApproval]}>
                  {getAbsenceStatusLabel(t, absence.absenceApproval)}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {canEdit && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isUpdating || absence.absenceApproval === AbsenceApproval.Rejected}
                      onClick={() => onUpdateStatus(absence, AbsenceApproval.Rejected)}
                    >
                      {t('buttons.reject', { ns: 'common' })}
                    </Button>
                    <Button
                      size="sm"
                      disabled={isUpdating || absence.absenceApproval === AbsenceApproval.Approved}
                      onClick={() => onUpdateStatus(absence, AbsenceApproval.Approved)}
                    >
                      {t('buttons.approve', { ns: 'common' })}
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          )
          })}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
